import { useEffect, useRef, useState } from "react";
import { Loader2, Maximize2, Minimize2, X } from "lucide-react";
import { api } from "@/lib/api";
import { useStars } from "@/lib/stars";
import { TooltipProvider } from "@/components/ui/tooltip";
import { VIEWS, VIEW_TAB } from "@/lib/c360Profile";
import { RATING } from "@/lib/creator360";
import { BlurCtx, CYAN, bl } from "@/lib/c360Mine";
import { MineEditor } from "@/components/c360/profile/MineEditor";
import { Header } from "@/components/c360/profile/Header";
import { VerdictBox } from "@/components/c360/profile/VerdictBox";
import { ComparePanel } from "@/components/c360/profile/ComparePanel";
import { BestAvenues } from "@/components/c360/profile/BestAvenues";
import { PromoHistory, TopPromo } from "@/components/c360/profile/YouTubeExtras";
import { IgReels, LivesTable, TopLives } from "@/components/c360/profile/LiveExtras";
import { ReserveCheckSlot } from "@/components/admin/ReserveCheckSlot";
import { useDemoBlur } from "@/lib/demoBlur";
import { useMainProfile } from "@/components/live/LiveHeader";
import { LiveProfileLink } from "@/components/live/LiveDrawer";
import { LiveProfileBody } from "@/components/live/LiveProfileBody";

function emptyText(av, d) {
  const x = d.av[av];
  const c = d.creator;
  if (av === "yt") return /not read/i.test(x.label) ? "Their standard videos are not read yet, so this view is blank for now." : "Their YouTube is not judged yet.";
  if (av === "live") return /does not/i.test(x.label) || d.nums.live.group === "never" ? "They do not go live." : "No live stream has been measured for them yet. This view stays blank until Live Watch measures their streams.";
  if (av === "ig") { const h = (c.ig || "").match(/instagram\.com\/([^/?#]+)/); return h ? `Their Instagram (@${h[1]}) is found, but its numbers could not be read yet, so this view is blank.` : "No Instagram account found for them yet, so this view is blank."; }
  return c.tg && d.nums.tg.subs ? `Their Telegram (${c.tg.replace(/^https:\/\//, "")}) is found, but its posts could not be read yet, so this view is blank.` : "No Telegram channel found for them yet, so this view is blank.";
}

const FOOT = {
  ig: "Promos on Instagram are not tracked yet, so their promo history in the other views is from YouTube only.",
  tg: "Promos on Telegram are not tracked yet, so their promo history in the other views is from YouTube only.",
};

function View({ view, d, setView, onEdit, onLive }) {
  if (view === "best") return <BestAvenues d={d} onOpen={setView} onEdit={onEdit} />;
  const live = view === "live" && <LiveProfileLink id={d.creator.id} onOpen={onLive} testid="c360-view-live-profile" className="self-start" />;
  if (d.av[view].r === "unknown") {
    return (<><VerdictBox av={view} d={d} onEdit={onEdit} />{live}<div className="rounded-lg border border-dashed border-zinc-700 p-6 text-sm text-zinc-400" data-testid={`c360-empty-${view}`}>{emptyText(view, d)}</div></>);
  }
  return (
    <>
      <VerdictBox av={view} d={d} onEdit={onEdit} />
      {live}
      <ComparePanel key={`${d.creator.id}-${view}`} av={view} d={d} />
      {view === "yt" && <><TopPromo d={d} /><PromoHistory d={d} /></>}
      {view === "live" && <><TopLives d={d} /><LivesTable d={d} /></>}
      {view === "ig" && <IgReels d={d} />}
      {FOOT[view] && <p className="mono text-[11px] text-zinc-500" data-testid={`c360-foot-${view}`}>{FOOT[view]}</p>}
    </>
  );
}

function TabDot({ v, d }) {
  if (v === "best" || !d?.ready) return null;
  const mr = d.mine?.[v]?.r;
  return <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ background: mr ? CYAN : RATING[d.av[v].r].color }} data-testid={`c360-tab-dot-${v}`} data-mine={mr ? "1" : "0"} />;
}

export function Creator360Profile({ id, view, from = "c360_list", onView, onClose, onLeave, onMine }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [wide, setWide] = useState(false);
  const [edit, setEdit] = useState(null);
  // v83 — "View live profile" swaps this drawer's content to the live profile; never a drawer on a drawer.
  const [live, setLive] = useState(false);
  const blurred = useDemoBlur().isBlurred(id);
  const goMain = useMainProfile(id, d?.creator?.seg === "unaffiliated", onLeave, onClose);
  const stars = useStars("c360");
  const sent = useRef({ first: null, last: null });
  // v80 — a client's open counts like any profile open; each view looked at is recorded too.
  useEffect(() => {
    if (!d?.ready || d.admin || d.creator?.id !== id) return;
    const k = `${id}|${view}`;
    if (sent.current.last === k) return;
    const first = sent.current.first !== id;
    sent.current = { first: id, last: k };
    api.post("/creator360/opened", { id, view, first, from_page: from }).catch(() => {});
  }, [d, id, view, from]);
  useEffect(() => {
    let alive = true;
    setD(null);
    setErr(false);
    setEdit(null);
    setLive(false);
    api.get(`/creator360/profile/${id}`).then((r) => { if (alive) setD(r.data); }).catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [id]);
  useEffect(() => {
    const k = (e) => e.key === "Escape" && !document.querySelector("[data-radix-popper-content-wrapper], [role=\"dialog\"]") && onClose();
    window.addEventListener("keydown", k);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = ""; };
  }, [onClose]);
  const onSaved = (av, m) => {
    setD((cur) => { const mine = { ...(cur.mine || {}) }; if (m) mine[av] = m; else delete mine[av]; return { ...cur, mine }; });
    if (onMine) onMine();
  };
  return (
    <BlurCtx.Provider value={blurred}>
    <TooltipProvider>
      <div className="fixed inset-0 z-50" data-testid="c360-profile" data-blurred={blurred ? "1" : "0"}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm c360-fade" onClick={onClose} data-testid="c360-profile-backdrop" />
        <aside className={`absolute right-0 top-0 h-full overflow-y-auto border-l hairline c360-slide transition-[width] duration-300 ${wide ? "w-full" : "w-full lg:w-[min(1240px,92vw)]"}`} style={{ background: "var(--bg, #0A0A0B)" }} data-testid="c360-drawer" data-wide={wide ? "1" : "0"}>
          <div className="sticky top-0 z-10 border-b hairline backdrop-blur-xl" style={{ background: "rgba(10,10,11,0.85)" }}>
            <div className="flex items-center justify-between gap-3 px-4 sm:px-6 pt-3">
              {live
                ? <button type="button" onClick={() => setLive(false)} className="text-sm text-zinc-300 hover:text-cyan-300 transition-colors" data-testid="c360-back-to-360">← Back to Creator 360</button>
                : <span className="mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 truncate">Creator 360{d?.creator ? <> · <span className={bl(blurred)} data-testid="c360-top-name">{d.creator.name}</span></> : ""}</span>}
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setWide((w) => !w)} className="hidden lg:inline-flex h-8 w-8 items-center justify-center rounded-md border hairline text-zinc-300 hover:border-white/30" title={wide ? "Back to drawer" : "Full screen"} data-testid="c360-expand">{wide ? <Minimize2 size={14} /> : <Maximize2 size={14} />}</button>
                <button type="button" onClick={onClose} className="h-8 px-3 inline-flex items-center gap-1.5 rounded-md border hairline text-sm text-zinc-200 hover:border-white/30" data-testid="c360-close"><X size={14} /> Close</button>
              </div>
            </div>
            {!live && <nav className="flex gap-1 px-4 sm:px-6 mt-2 overflow-x-auto" data-testid="c360-tabs">
              {VIEWS.map((v) => (
                <button key={v} type="button" onClick={() => onView(v)} data-testid={`c360-tab-${v}`} data-on={v === view ? "1" : "0"}
                  className={`px-3 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${v === view ? "border-cyan-400 text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}><TabDot v={v} d={d} />{VIEW_TAB[v]}</button>
              ))}
            </nav>}
            {live && <div className="h-3" />}
          </div>
          {live && <LiveProfileBody id={id} enabled inDrawer onLeave={onLeave} onClose={onClose} className="px-4 sm:px-6 py-6" />}
          {!live && <div className="px-4 sm:px-6 py-6 flex flex-col gap-6">
            {err && <div className="text-zinc-400 py-10" data-testid="c360-profile-error">This profile could not be opened.</div>}
            {!err && !d && <div className="flex items-center gap-2 text-zinc-500 py-10" data-testid="c360-profile-loading"><Loader2 size={16} className="animate-spin" /> Loading profile…</div>}
            {d && !d.ready && <div className="text-zinc-400 py-10" data-testid="c360-profile-not-ready">Creator 360 is loading. Please check back in a minute.</div>}
            {d?.ready && (
              <div key={view} className="flex flex-col gap-6 c360-rise" data-testid={`c360-view-${view}`}>
                <Header view={view} d={d} star={{ on: stars.ids?.has(id), toggle: () => stars.toggle(id) }} onOpen={onView} onMain={goMain} />
                <View view={view} d={d} setView={onView} onEdit={setEdit} onLive={() => setLive(true)} />
                <ReserveCheckSlot id={id} />
              </div>
            )}
          </div>}
        </aside>
      </div>
      {edit && d?.ready && <MineEditor av={edit} d={d} onClose={() => setEdit(null)} onSaved={onSaved} />}
    </TooltipProvider>
    </BlurCtx.Provider>
  );
}
